import React, { useState, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { MicVAD } from '@ricky0123/vad-web';
import { Mic, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FOREST_GREEN, MID_GRAY, WHITE, CHARCOAL, LIGHT_GRAY, SHADOWS } from '@/styles/tokens';
import { loadSpeakerModel, extractEmbedding } from '@/services/voice/SpeakerVerification';
import { enrollVoice } from '@/services/api/voiceApi';
import type { VoiceEnrollment } from '@/services/voice/types';

const ECAPA_MODEL_URL = '/models/ecapa_tdnn.onnx';
const TARGET_SAMPLES = 3;
const SAFETY_TIMEOUT_MS = 10000;

const ENROLLMENT_PHRASES = [
  'Open the project dashboard',
  'Show me the latest design',
  'Create a new page layout',
];

interface VoiceEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (enrollment: VoiceEnrollment) => void;
}

type EnrollmentStep = 'intro' | 'loading-model' | 'recording' | 'saving' | 'done' | 'error';

export function VoiceEnrollmentDialog({ open, onOpenChange, onComplete }: VoiceEnrollmentDialogProps) {
  const [step, setStep] = useState<EnrollmentStep>('intro');
  const [samplesCollected, setSamplesCollected] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [speechDetected, setSpeechDetected] = useState(false);
  const [misfire, setMisfire] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const embeddingsRef = useRef<number[][]>([]);
  const vadRef = useRef<any>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanupVAD = useCallback(() => {
    if (vadRef.current) {
      vadRef.current.destroy().catch(() => {});
      vadRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    cleanupVAD();
    setStep('intro');
    setSamplesCollected(0);
    setIsRecording(false);
    setSpeechDetected(false);
    setMisfire(false);
    setError(null);
    embeddingsRef.current = [];
  }, [cleanupVAD]);

  const startEnrollment = useCallback(async () => {
    try {
      setStep('loading-model');
      await loadSpeakerModel(ECAPA_MODEL_URL);
      setStep('recording');
    } catch (err) {
      setError(`Failed to load voice model: ${err}`);
      setStep('error');
    }
  }, []);

  const processRecordedAudio = useCallback(async (audio: Float32Array) => {
    try {
      console.log('[Enrollment] Extracting embedding from', audio.length, 'samples');
      const embedding = await extractEmbedding(audio);
      embeddingsRef.current.push(embedding);

      const newCount = embeddingsRef.current.length;
      console.log('[Enrollment] Embedding extracted, sample', newCount, 'of', TARGET_SAMPLES);
      setSamplesCollected(newCount);
      setIsRecording(false);
      setSpeechDetected(false);

      if (newCount >= TARGET_SAMPLES) {
        setStep('saving');
        const response = await enrollVoice(embeddingsRef.current);
        if (response.success) {
          setStep('done');
          onComplete({
            embeddings: embeddingsRef.current,
            enrollmentCount: embeddingsRef.current.length,
            isActive: true,
            verificationThreshold: response.data?.verificationThreshold ?? 0.65,
          });
        } else {
          throw new Error(response.message || 'Failed to save enrollment');
        }
      }
    } catch (err) {
      setIsRecording(false);
      setError(String(err));
      setStep('error');
    }
  }, [onComplete]);

  const recordSample = useCallback(async () => {
    try {
      setIsRecording(true);
      setSpeechDetected(false);
      setMisfire(false);

      const vad = await MicVAD.new({
        baseAssetPath: '/vad/',
        onnxWASMBasePath: '/vad/',
        model: 'legacy',
        startOnLoad: false,
        // Tighter tuning for short directed phrases
        positiveSpeechThreshold: 0.6,
        negativeSpeechThreshold: 0.35,
        redemptionMs: 800,
        preSpeechPadMs: 300,
        minSpeechMs: 400,

        onSpeechStart: () => {
          console.log('[Enrollment] Speech started');
          setSpeechDetected(true);
          setMisfire(false);
        },

        onSpeechEnd: async (audio: Float32Array) => {
          console.log('[Enrollment] Speech ended, samples:', audio.length,
            'duration:', (audio.length / 16000).toFixed(1) + 's');
          // Pause first — don't destroy inside callback (can abort execution)
          if (vadRef.current) {
            vadRef.current.pause();
          }
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          await processRecordedAudio(audio);
          // Safe to destroy after processing
          if (vadRef.current) {
            vadRef.current.destroy().catch(() => {});
            vadRef.current = null;
          }
        },

        onVADMisfire: () => {
          console.log('[Enrollment] Misfire (speech too short)');
          setMisfire(true);
          setSpeechDetected(false);
        },
      });

      vadRef.current = vad;
      await vad.start();

      // Safety timeout — error if no speech detected within 10s
      timeoutRef.current = setTimeout(() => {
        if (vadRef.current === vad) {
          cleanupVAD();
          setIsRecording(false);
          setSpeechDetected(false);
          setError('No speech detected. Please try again.');
          setStep('error');
        }
      }, SAFETY_TIMEOUT_MS);
    } catch (err) {
      cleanupVAD();
      setIsRecording(false);
      setError(String(err));
      setStep('error');
    }
  }, [cleanupVAD, processRecordedAudio]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) reset();
    onOpenChange(newOpen);
  };

  const currentPhrase = ENROLLMENT_PHRASES[samplesCollected] || ENROLLMENT_PHRASES[0];

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998,
        }} />
        <Dialog.Content style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          backgroundColor: WHITE, borderRadius: '12px', padding: '32px',
          width: '440px', maxWidth: '90vw', zIndex: 9999,
          boxShadow: SHADOWS.xl,
        }}>
          <Dialog.Title style={{ fontSize: '18px', fontWeight: 600, color: CHARCOAL.DEFAULT, marginBottom: '8px' }}>
            Voice Enrollment
          </Dialog.Title>

          {step === 'intro' && (
            <>
              <Dialog.Description style={{ fontSize: '14px', color: MID_GRAY.DEFAULT, marginBottom: '24px', lineHeight: 1.5 }}>
                To use voice commands, we need to learn your voice. You'll say {TARGET_SAMPLES} short
                phrases out loud. This helps the system recognize you and only accept commands from
                authorized users. All processing happens on your device.
              </Dialog.Description>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Dialog.Close asChild>
                  <Button variant="outline" size="sm">Cancel</Button>
                </Dialog.Close>
                <Button
                  size="sm"
                  onClick={startEnrollment}
                  style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: WHITE }}
                >
                  Begin Enrollment
                </Button>
              </div>
            </>
          )}

          {step === 'loading-model' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: FOREST_GREEN.DEFAULT, margin: '0 auto 16px' }} />
              <p style={{ color: MID_GRAY.DEFAULT, fontSize: '14px' }}>Loading voice recognition model...</p>
              <p style={{ color: MID_GRAY.lighter, fontSize: '12px', marginTop: '4px' }}>First time may take a moment to download (~25MB)</p>
            </div>
          )}

          {step === 'recording' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <p style={{ color: MID_GRAY.DEFAULT, fontSize: '14px', marginBottom: '8px' }}>
                Sample {samplesCollected + 1} of {TARGET_SAMPLES}
              </p>
              <p style={{
                color: CHARCOAL.DEFAULT, fontSize: '16px', fontWeight: 600,
                marginBottom: '16px', fontStyle: 'italic',
              }}>
                &ldquo;{currentPhrase}&rdquo;
              </p>

              {/* Progress dots */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '24px' }}>
                {Array.from({ length: TARGET_SAMPLES }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '12px', height: '12px', borderRadius: '50%',
                      backgroundColor: i < samplesCollected
                        ? FOREST_GREEN.DEFAULT
                        : i === samplesCollected && isRecording
                          ? (speechDetected ? '#ef4444' : '#f59e0b')
                          : LIGHT_GRAY.DEFAULT,
                      transition: 'background-color 200ms',
                    }}
                  />
                ))}
              </div>

              <Button
                size="lg"
                onClick={recordSample}
                disabled={isRecording}
                style={{
                  backgroundColor: isRecording
                    ? (speechDetected ? '#ef4444' : '#f59e0b')
                    : FOREST_GREEN.DEFAULT,
                  color: WHITE,
                  borderRadius: '50%',
                  width: '64px', height: '64px',
                  padding: 0,
                  animation: speechDetected ? 'voice-pulse 1s infinite' : undefined,
                }}
              >
                {isRecording
                  ? (speechDetected
                    ? <Loader2 className="w-6 h-6 animate-spin" />
                    : <Mic className="w-6 h-6" style={{ opacity: 0.8 }} />)
                  : <Mic className="w-6 h-6" />
                }
              </Button>

              <p style={{
                color: misfire ? '#f59e0b' : MID_GRAY.lighter,
                fontSize: '12px', marginTop: '12px',
                fontWeight: misfire ? 500 : 400,
              }}>
                {misfire
                  ? 'Too short \u2014 please say the full phrase'
                  : isRecording
                    ? (speechDetected ? 'Listening... will stop when you finish' : 'Waiting for speech...')
                    : 'Tap to start'
                }
              </p>

              <style>{`
                @keyframes voice-pulse {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
                  50% { box-shadow: 0 0 0 12px rgba(239,68,68,0); }
                }
              `}</style>
            </div>
          )}

          {step === 'saving' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: FOREST_GREEN.DEFAULT, margin: '0 auto 16px' }} />
              <p style={{ color: MID_GRAY.DEFAULT, fontSize: '14px' }}>Saving voice profile...</p>
            </div>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                backgroundColor: FOREST_GREEN.DEFAULT, margin: '0 auto 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check className="w-6 h-6" style={{ color: WHITE }} />
              </div>
              <p style={{ color: CHARCOAL.DEFAULT, fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                Voice Enrolled!
              </p>
              <p style={{ color: MID_GRAY.DEFAULT, fontSize: '14px', marginBottom: '24px' }}>
                You can now use voice commands. Press the mic button to start.
              </p>
              <Dialog.Close asChild>
                <Button size="sm" style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: WHITE }}>
                  Got it
                </Button>
              </Dialog.Close>
            </div>
          )}

          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <X className="w-8 h-8" style={{ color: '#ef4444', margin: '0 auto 16px' }} />
              <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>{error}</p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <Dialog.Close asChild>
                  <Button variant="outline" size="sm">Cancel</Button>
                </Dialog.Close>
                <Button size="sm" onClick={() => { reset(); startEnrollment(); }}
                  style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: WHITE }}>
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
